import type { ContextEngine, Config, CustomContext } from '@/types';

export function resolveStringOrPageContextFunction({
  value,
  pageContext,
  customContext,
  fieldName,
}: {
  value: Config.ValueTypes.StringOrContextResultFunction | undefined;
  pageContext: ContextEngine.ContextResult | null;
  customContext: CustomContext | null;
  fieldName?: string; // Optional field name for better error logging
}): string | undefined {
  if (typeof value === 'function') {
    try {
      // Execute function with context
      // Functions can access global scope (e.g., analytics, window, etc.)
      // Example: () => analytics.user().anonymousId()
      // Note: External dependencies like 'analytics' must be imported/available in widget context
      let result: unknown = value(
        pageContext as unknown as never,
        customContext as unknown as never
      );

      // Handle case where convertFunctionString wrapper returns another function
      // The wrapper from config.ts returns the original function, so we need to execute it
      if (typeof result === 'function') {
        result = (result as () => unknown)();
      }

      // Convert result to string (functions might return numbers, booleans, etc.)
      if (result === undefined || result === null) {
        return undefined;
      }

      // Convert to string - handles numbers, booleans, etc.
      return String(result);
    } catch (error) {
      const fieldContext = fieldName ? ` for hidden field "${fieldName}"` : '';
      
      // Extract error message
      let errorMessage = 'Unknown error';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      // Detect common error patterns to provide helpful hints
      let helpfulHint = '';
      if (
        errorMessage.includes('is not defined') ||
        errorMessage.includes('Cannot read') ||
        errorMessage.includes('undefined')
      ) {
        helpfulHint = ' (likely a missing dependency - ensure required libraries/variables are loaded in the widget context)';
      }
      
      // Log error with clear context
      console.warn(
        `[Widget] ⚠️ Failed to resolve function${fieldContext}: ${errorMessage}${helpfulHint}`
      );
      
      if (error instanceof Error && error.stack) {
        console.debug(`[Widget] Stack trace:`, error.stack);
      }
      
      return undefined;
    }
  }

  if (!value) return undefined;
  return String(value); // Plain string - ensure it's always a string
}
